/**
 * The transport interface. No implementation lives here.
 *
 * The browser implements it with `fetch` and a `ReadableStream` reader. The
 * conformance runner implements it with `node:http`. The state machine sees
 * only this interface, which is what lets the same machine run in both places
 * and is the reason every invariant proved in CI is proved about the code
 * that ships.
 *
 * Two deliberate departures from the obvious shape, both recorded in
 * docs/DECISIONS.md entry 16.
 *
 *   1. Cancellation is a package owned `CancelSignal`, not an `AbortSignal`.
 *      `AbortSignal` is a platform type, and this package compiles with
 *      neither the DOM library nor the Node types so that a platform
 *      dependency is a build error. Each transport bridges the signal to
 *      whatever its environment uses.
 *   2. `TransportHttpError` is the typed channel for a connection the server
 *      refused. The reconnect policy needs the status and the `Retry-After`
 *      value from a 429, and an async iterable of bytes has nowhere else to
 *      put them.
 */

export interface StreamRequest {
  url: string;
  /** When present, the request is a POST carrying this as a JSON body. Otherwise a GET. */
  body?: unknown;
  /**
   * The resume cursor, sent as the `Last-Event-ID` header. A string because
   * the cursor is opaque on the wire; this server's integers are its own
   * choice. See docs/DECISIONS.md entry 8.
   */
  lastEventId?: string;
  /** Aborting this must end the iteration promptly. */
  signal: CancelSignal;
}

export interface StreamTransport {
  /**
   * Open a connection and yield its bytes as they arrive. Connection failures
   * and non success HTTP responses surface as a throw from the iteration,
   * `TransportHttpError` where a status is known.
   */
  open(request: StreamRequest): AsyncIterable<Uint8Array>;
}

/** A read only view of a cancellation. The machine holds the writable half. */
export interface CancelSignal {
  readonly aborted: boolean;
  /**
   * Register a listener. Fires at most once. When the signal is already
   * aborted the listener fires synchronously. Returns an unsubscribe.
   */
  onAbort(listener: () => void): () => void;
}

export interface CancelSource {
  readonly signal: CancelSignal;
  cancel(): void;
}

/** The writable half of a `CancelSignal`. */
export function createCancelSource(): CancelSource {
  let aborted = false;
  const listeners = new Set<() => void>();

  const signal: CancelSignal = {
    get aborted() {
      return aborted;
    },
    onAbort(listener: () => void): () => void {
      if (aborted) {
        listener();
        return () => undefined;
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };

  return {
    signal,
    cancel(): void {
      if (aborted) return;
      aborted = true;
      for (const listener of listeners) listener();
      listeners.clear();
    },
  };
}

/**
 * A connection the server answered with a non success status. `retryAfterSeconds`
 * is present when the response carried a usable `Retry-After` header, and the
 * reconnect policy honours it without spending an attempt.
 */
export class TransportHttpError extends Error {
  readonly status: number;
  readonly retryAfterSeconds: number | undefined;

  constructor(status: number, options: { retryAfterSeconds?: number; message?: string } = {}) {
    super(
      options.message ??
        `The server answered ${String(status)} instead of opening a stream.` +
          (options.retryAfterSeconds === undefined
            ? ''
            : ` Retry after ${String(options.retryAfterSeconds)} seconds.`),
    );
    this.name = 'TransportHttpError';
    this.status = status;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }
}
